/**
 * GloveMode — Context + Hook per rilevamento automatico "Guanti" / Riding Mode
 *
 * Attiva automaticamente una UI adattiva quando il rider è in movimento:
 * - Bottoni 88px, solo icone grandi, niente testo piccolo
 * - Feedback aptico (vibrazione) invece di popup
 * - Alto contrasto automatico in base alla luce ambientale
 * - HUD overlay con velocità e navigazione
 * - Tema adattivo day/night/tunnel
 *
 * Attivazione: GPS speed > 5 km/h OPPURE toggle manuale
 */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";

export type RidingTheme = "day" | "night" | "tunnel";

interface GloveContextValue {
  isActive: boolean;
  /** Alias di isActive per retrocompatibilità */
  isGloveMode: boolean;
  isHighContrast: boolean;
  /** Velocità corrente in km/h */
  speedKmh: number;
  /** Livello luminosità ambientale normalizzato (0-100), -1 se non disponibile */
  ambientLight: number;
  /** Tema consigliato in base alla luce ambientale */
  ridingTheme: RidingTheme;
  toggle: () => void;
  /** Alias di toggle per retrocompatibilità */
  toggleGloveMode: () => void;
  enable: () => void;
  disable: () => void;
}

const GloveContext = createContext<GloveContextValue>({
  isActive: false,
  isGloveMode: false,
  isHighContrast: false,
  speedKmh: 0,
  ambientLight: -1,
  ridingTheme: "day",
  toggle: () => {},
  toggleGloveMode: () => {},
  enable: () => {},
  disable: () => {},
});

export function GloveModeProvider({ children }: { children: React.ReactNode }) {
  const [isActive, setIsActive] = useState(false);
  const [isHighContrast, setIsHighContrast] = useState(false);
  const [speedKmh, setSpeedKmh] = useState(0);
  const [ambientLight, setAmbientLight] = useState(-1);
  const [ridingTheme, setRidingTheme] = useState<RidingTheme>("day");

  // Debounce per prevenire toggle rapidi delle classi CSS
  const lastToggleRef = useRef(0);

  // ─── GPS Speed detection (SOLO speedometro) ────────────────────────
  // FIX RIDING-MODE (02 Set 2026): l'auto-attivazione della riding/glove
  // mode è stata RIMOSSA DEL TUTTO. Per un anno i falsi positivi del GPS
  // (fix spurii indoor) attivavano la modalità da soli, esplodendo l'intera
  // UI (bottoni 88px, icone 32px, testi nascosti) in ogni menu. Ora la
  // modalità è SOLO manuale (toggle esplicito dell'utente): l'app non
  // cambia MAI aspetto da sola, nemmeno in marcia. Qui resta solo il
  // monitoraggio della velocità (speedKmh) per i consumatori del context.
  useEffect(() => {
    let watchId: number | null = null;

    // FIX CONSOLE: non avviare il watch GPS sulle pagine di auth.
    // Il watchPosition chiede il permesso geolocalizzazione sul login/register
    // → "User denied Geolocation" / timeout in console ad ogni visita.
    const isAuthPage =
      window.location.pathname.startsWith("/login") ||
      window.location.pathname.startsWith("/register") ||
      window.location.pathname.startsWith("/forgot") ||
      window.location.pathname.startsWith("/reset-password") ||
      window.location.pathname === "/oauth-success";
    if (isAuthPage) return;

    // FIX CONSOLE: deduplica i warning GPS per code (1 solo log per codice/sessione)
    const gpsWarnedCodes = new Set<number>();

    if ("geolocation" in navigator) {
      watchId = navigator.geolocation.watchPosition(
        (pos) => {
          const speed = pos.coords?.speed ?? 0; // m/s
          setSpeedKmh(Math.round(speed * 3.6));
        },
        (error: GeolocationPositionError) => {
          // FIX BUG #14: Non ignorare silenziosamente gli errori GPS.
          // error.code: 1=permission denied, 2=position unavailable, 3=timeout
          // FIX CONSOLE: code 1 (permesso negato dall'utente) e' una
          // condizione ATTESA su desktop (l'utente ha scelto di non
          // condividere) → log debug, non warning. I codici 2/3 restano warn.
          if (error.code !== 1 && !gpsWarnedCodes.has(error.code)) {
            gpsWarnedCodes.add(error.code);
            console.warn("[GloveModeContext] GPS error:", {
              code: error.code,
              message: error.message,
            });
          } else if (error.code === 1 && !gpsWarnedCodes.has(error.code)) {
            gpsWarnedCodes.add(error.code);
            console.debug(
              "[GloveModeContext] GPS permission denied (scelta utente, atteso)",
            );
          }
          if (error.code === 1) {
            // Permission denied — l'utente ha negato, non riproviamo
            if (watchId !== null) {
              navigator.geolocation.clearWatch(watchId);
              watchId = null;
            }
          }
        },
        { enableHighAccuracy: false, maximumAge: 5000, timeout: 10000 },
      );
    }

    return () => {
      if (watchId !== null) navigator.geolocation.clearWatch(watchId);
    };
  }, []);

  // ─── Ambient Light Sensor → contrast + theme ────────────────────────
  useEffect(() => {
    if ("AmbientLightSensor" in window) {
      try {
        const sensor = new (window as any).AmbientLightSensor({ frequency: 1 });
        sensor.onreading = () => {
          const lux: number = sensor.illuminance ?? 500;
          const normalized = Math.min(100, Math.max(0, Math.round(lux / 100)));
          setAmbientLight(normalized);

          // High contrast above 8000 lux (bright sunlight)
          setIsHighContrast(lux > 8000);

          // Riding theme based on ambient light
          if (normalized <= 5) {
            setRidingTheme("tunnel");
          } else if (normalized <= 15) {
            setRidingTheme("night");
          } else {
            setRidingTheme("day");
          }
        };
        sensor.onerror = () => {};
        sensor.start();

        return () => {
          try {
            sensor.stop();
          } catch {}
        };
      } catch {
        /* not supported */
      }
    }
  }, []);

  const toggle = useCallback(() => setIsActive((v) => !v), []);
  const enable = useCallback(() => setIsActive(true), []);
  const disable = useCallback(() => setIsActive(false), []);

  // ─── Apply CSS classes to body ──────────────────────────────────────
  useEffect(() => {
    const body = document.body;
    const html = document.documentElement;

    // FIX (Luglio 2026): Resetta SEMPRE il font-size della root a 16px
    // prima di applicare/togliere riding-mode. Previene accumulo rem.
    html.style.fontSize = "16px";

    // FIX BUG #25: Debounce 300ms per prevenire toggle rapidi (es. a ogni fix GPS)
    const now = Date.now();
    if (now - lastToggleRef.current < 300) return;
    lastToggleRef.current = now;

    if (isActive) {
      // FIX (Luglio 2026): Forza reflow prima di aggiungere riding-mode
      // così il browser riparte da 16px e applica 24px pulitamente.
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      void body.offsetHeight;

      // FIX BUG #25: Verifica se già presente PRIMA di aggiungere.
      // Su Android WebView, aggiungere una classe già presente può
      // innescare un ricalcolo CSS che, con il bug del rem, accumula.
      if (!body.classList.contains("riding-mode")) {
        body.classList.add("riding-mode");
      }
      if (!body.classList.contains("glove-mode")) {
        body.classList.add("glove-mode");
      }
    } else {
      body.classList.remove("glove-mode", "riding-mode");
    }

    if (isHighContrast) {
      if (!body.classList.contains("high-contrast")) {
        body.classList.add("high-contrast");
      }
    } else {
      body.classList.remove("high-contrast");
    }

    // Riding theme class
    body.classList.remove(
      "riding-mode--day",
      "riding-mode--night",
      "riding-mode--tunnel",
    );
    if (isActive) {
      const themeClass = `riding-mode--${ridingTheme}`;
      if (!body.classList.contains(themeClass)) {
        body.classList.add(themeClass);
      }
    }
  }, [isActive, isHighContrast, ridingTheme]);

  return (
    <GloveContext.Provider
      value={{
        isActive,
        isHighContrast,
        speedKmh,
        ambientLight,
        ridingTheme,
        toggle,
        enable,
        disable,
        isGloveMode: isActive,
        toggleGloveMode: toggle,
      }}
    >
      {children}
    </GloveContext.Provider>
  );
}

export function useGloveMode() {
  return useContext(GloveContext);
}

export default GloveModeProvider;
